import { Injectable } from '@angular/core';
import { BaseService } from '@app/core/services/base.service';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { HttpUtilService } from "@services/http.util.service";
import { SubscriptionFilter } from './models/subscription/subscription-filter.model';
import { ServiceOffer } from './models/service-offer/service-offer.model';
import { Page } from '../commons/page';
import { catchError, map } from 'rxjs/operators';
import { SubscriptionDTO } from './models/subscription/subscription.model';
import { NotificationService } from '../util/notification.service';
import { SubscriptionStatusEnum } from './models/enum/subscription-status.enum';
import { SubscriptionStatusesHistory } from './models/subscription/subscription-status-history.model';
import moment from 'moment';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService extends BaseService {
  private apiUrl = environment.api.mobilityUrl;

  constructor(private httpClient: HttpClient,
              protected notificationService: NotificationService,
              protected translate: TranslateService,
              private httpUtilService: HttpUtilService,
              private keycloakService: KeycloakService
  ) {
    super(notificationService, translate);
  }


  /* SERVICE OFFER */

  getServiceOffers(): Observable<Page<ServiceOffer>> {
    const headers = this.httpUtilService.buildHeaders();
    return this.httpClient.get<Page<ServiceOffer>>(`${this.apiUrl}/api/subscription-manager/v1/service-offers`, { headers });
  }

  getServiceOfferByReference(serviceOfferName: string): Observable<ServiceOffer> {
    if (!serviceOfferName) {
      throw new Error('Required parameter serviceOfferName was null or undefined when calling getServiceOfferByReference.');
    }

    const headers = this.httpUtilService.buildHeaders();
    const params = new HttpParams().set('reference', serviceOfferName);
    return this.httpClient.get<Page<ServiceOffer>>(`${this.apiUrl}/api/subscription-manager/v1/service-offers`, { headers, params }).pipe(
      map((serviceOffers: Page<ServiceOffer>) => {
        return serviceOffers.content[0];
      })
    );
  }

  /* SUBSCRIPTION */

  getAllSubscriptions(filter: SubscriptionFilter, sortDirection = 'asc', sortActive = '', page = 0, size = 10): Observable<Page<SubscriptionDTO>> {
    const url = `${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions`;

    const normalizedSortElement = this.httpUtilService.getSortElement(sortActive, {
      ID: 'subscriptionId',
      HOLDER_EMAIL: 'subscriptionHolder.contactDetails.emailAddress',
      HOLDER_FIRST_NAME: 'subscriptionHolder.firstName',
      HOLDER_LAST_NAME: 'subscriptionHolder.lastName',
      SUBSCRIBED_SERVICE_OFFER_NAME: 'serviceOfferName'
    });

    const sort = normalizedSortElement + ',' + sortDirection;
    let params = this.httpUtilService.buildHttpParams({
      page: page.toString(),
      size: size.toString(),
      sort: sort
    });

    if (filter?.subscriptionHolderAccountId) {
      params = params.set('subscriptionHolderAccountId', filter.subscriptionHolderAccountId);
    }

    if (filter?.subscriptionPayerAccountId){
      params = params.set('subscriptionPayerAccountId', filter.subscriptionPayerAccountId);
    }

    if (filter?.serviceOfferReference) {
      params = params.set('serviceOfferReference', filter.serviceOfferReference);
    }

    if (filter?.status) {
      params = params.set('status', filter.status);
    }

    if (filter?.paymentMode) {
      params = params.set('paymentMode', filter.paymentMode);
    }

    return this.httpClient.get<Page<SubscriptionDTO>>(url, {
      params,
      headers: this.httpUtilService.buildHeaders()
    });
  }

  getSubscriptionById(subscriptionId: number): Observable<SubscriptionDTO> {
    return this.httpClient.get<SubscriptionDTO>(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}`);
  }

  getAllSubscriptionsByPayerAccountId(payerAccountId: number, sortOrder = 'asc', sortElement = '', page = 0, size = 20): Observable<Page<SubscriptionDTO>> {
    const url = `${environment.api.mobilityUrl}/api/subscription-manager/v1/payer/${payerAccountId}/subscriptions`;
    let sort = '';

    if (sortOrder !== '' && sortElement !== '') {
      sort = sortElement + ',' + sortOrder;
    }
    let params = this.httpUtilService.buildHttpParams({
      page: page.toString(),
      size: size.toString(),
      sort: sort
    });

    return this.httpClient.get<Page<SubscriptionDTO>>(url, {
      params,
      headers: this.httpUtilService.buildHeaders()
    });
  }

  getSubscriptionByPayerAccountIdAndSubscriptionId(payerAccountId: number, subscriptionId: number): Observable<SubscriptionDTO> {
    const url = `${environment.api.mobilityUrl}/api/subscription-manager/v1/payer/${payerAccountId}/subscriptions/${subscriptionId}`;
    return this.httpClient.get<SubscriptionDTO>(url, {headers: this.httpUtilService.buildHeaders()}).pipe(
      catchError(_err => {
        console.error(_err);
        return throwError(() => _err);
      })
    );
  }

  deactivateSubscription(subscriptionId: number, reasons: string[]): Observable<SubscriptionDTO> {
    const headers = new HttpHeaders()
      .set('actorReference', this.keycloakService.getKeycloakInstance().subject)
      .set('actorType', this.keycloakService.getKeycloakInstance().realm);

    return this.httpClient.put<SubscriptionDTO>(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}/_deactivate`, reasons, { headers })
  }

  unblockSubscription(subscriptionId: number, reasons: string[]) {
    const headers = new HttpHeaders()
      .set('actorReference', this.keycloakService.getKeycloakInstance().subject)
      .set('actorType', this.keycloakService.getKeycloakInstance().realm);

    return this.httpClient.put<SubscriptionDTO>(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}/_unblock`, reasons, { headers })
  }

  terminateSubscription(subscriptionId: number, reasons: string[]) {
    const headers = new HttpHeaders()
      .set('actorReference', this.keycloakService.getKeycloakInstance().subject)
      .set('actorType', this.keycloakService.getKeycloakInstance().realm);

    return this.httpClient.put<SubscriptionDTO>(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}/_terminate`, reasons, { headers })
  }

  updateSubscriptionStatus(targetStatus: SubscriptionStatusEnum, subscriptionId: number, reasons: string[]) {
    switch (targetStatus) {
      case SubscriptionStatusEnum.ACTIVE:
        return this.unblockSubscription(subscriptionId, reasons);
      case SubscriptionStatusEnum.INACTIVE:
        return this.deactivateSubscription(subscriptionId, reasons);
      case SubscriptionStatusEnum.TERMINATED:
        return this.terminateSubscription(subscriptionId, reasons);
      default:
        throw new Error('Target status is incorrect, to update, the target status must be ACTIVE, INACTIVE or TERMINATED');
    }
  }

  addSubscriptionStatusHistory(subscriptionId: number, status: SubscriptionStatusEnum, effectiveDate: Date, reason: string[] = []): Observable<SubscriptionStatusesHistory> {
    let params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('status', status)
      .set('effectiveDate', moment(effectiveDate).format("YYYY-MM-DDTHH:mm:ss.SSSZZ"));

    if (reason) {
      reason.forEach(r => {
        params = params.append('reason', r);
      })
    }

    return this.httpClient.put<SubscriptionStatusesHistory>(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}/status-changes`, null, { params }).pipe(
      catchError(_err => {
        console.error(_err);
        return throwError(() => _err);
      })
    );
  }

  deleteSubscriptionStatusHistory(subscriptionId: number, statusChangeId: number) {
    return this.httpClient.delete(`${environment.api.mobilityUrl}/api/subscription-manager/v1/subscriptions/${subscriptionId}/status-changes/${statusChangeId}`).pipe(
      catchError(_err => {
        console.error(_err);
        return throwError(() => _err);
      })
    );
  }

}
